import type { PressableProps } from "../../primitives";
import { ctaFabLineHeight, ctaFabLineHeightDesktop, getCtaFabBoxSize } from "../../tokens";
import type { FabButtonSize } from "./types";

/** FabButton size → the shared Button `size` key (used for `buttonVariants` + `button/lib` maps). */
export const fabButtonSizeToken: Record<FabButtonSize, "sm" | "md" | "lg"> = {
  small: "sm",
  medium: "md",
  large: "lg",
};

/**
 * Icon-only FAB, from Figma's `_cta/line-height/fab-*`: a box of the icon line
 * plus the CTA padding (32/40/48, desktop 40/44/56) and the icon on that line
 * (20/24/32, desktop 24/28/36). A labelled FAB sizes like Button.
 */
export const fabIconOnlyBoxClass: Record<"sm" | "md" | "lg", string> = {
  sm: "h-cta-fab-sm w-cta-fab-sm md:h-cta-fab-sm-dt md:w-cta-fab-sm-dt",
  md: "h-cta-fab-md w-cta-fab-md md:h-cta-fab-md-dt md:w-cta-fab-md-dt",
  lg: "h-cta-fab-lg w-cta-fab-lg md:h-cta-fab-lg-dt md:w-cta-fab-lg-dt",
};

const slopTo44 = (box: number) => Math.max(0, (44 - box) / 2);

/**
 * An icon-only FAB's native tap area: the `hitSlop` on each side that brings
 * its phone box (32/40/48) to 44pt, and none on a box already that big
 * (6/2/0). A labelled FAB sizes like Button, so it keeps `buttonHitSlop`.
 * Phone values: from 768 wide the circle is 40/44/56, so there its area is
 * 52/48/56, still at least 44.
 */
export const fabIconOnlyHitSlop: Record<"sm" | "md" | "lg", number> = {
  sm: slopTo44(getCtaFabBoxSize("sm")),
  md: slopTo44(getCtaFabBoxSize("md")),
  lg: slopTo44(getCtaFabBoxSize("lg")),
};

export const fabIconOnlyIconPx: Record<"sm" | "md" | "lg", { mobile: number; desktop: number }> = {
  sm: { mobile: ctaFabLineHeight.sm, desktop: ctaFabLineHeightDesktop.sm },
  md: { mobile: ctaFabLineHeight.md, desktop: ctaFabLineHeightDesktop.md },
  lg: { mobile: ctaFabLineHeight.lg, desktop: ctaFabLineHeightDesktop.lg },
};

/** An icon-only Success FAB's icon, from Figma's `Fab / Success - Only Icon` (`8166:8386`) */
export const fabSuccessIconClass = "text-luce-button-success-default";

type FabButtonAccessibilityContext = {
  label?: string;
  icon?: string;
  loading: boolean;
  disabled: boolean;
} & Pick<PressableProps, "accessibilityLabel" | "accessibilityState">;

export function getFabButtonAccessibilityLabel(context: FabButtonAccessibilityContext): string {
  const baseLabel = context.accessibilityLabel ?? context.label ?? context.icon ?? "Button";
  // i18n: button.loadingSuffix
  return context.loading ? `${baseLabel} Loading` : baseLabel;
}

export function getFabButtonAccessibilityState(
  context: FabButtonAccessibilityContext,
): PressableProps["accessibilityState"] {
  return {
    ...context.accessibilityState,
    disabled: context.disabled,
    busy: context.loading,
  };
}
